import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save, Percent, Hash, PoundSterling } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const field = 'mt-1 h-12 bg-[#1E2530] border-white/20 text-white';
const CIS_OPTIONS = [
  { value: '20', label: '20%', hint: 'Standard — most subcontractors' },
  { value: '30', label: '30%', hint: 'Not registered / higher rate' },
  { value: '0', label: 'Gross', hint: '0% — gross payment status' },
];
export const fmtNumber = (prefix, padding, next) => `${prefix || 'INV'}${String(next || 1).padStart(Number(padding) || 3, '0')}`;

/** Profile → CIS, default day rate, payment terms and invoice numbering (brief §4, §5.5). */
export function InvoiceSettingsCard({ api, profile, onSaved }) {
  const [form, setForm] = useState({ cis_status: '20', invoice_day_rate: '', payment_terms_days: 14, prefix: 'INV', padding: 3, next: 1 });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!profile) return;
    const n = profile.invoice_numbering || {};
    setForm({
      cis_status: profile.cis_status || '20',
      invoice_day_rate: profile.invoice_day_rate ?? '',
      payment_terms_days: profile.payment_terms_days ?? 14,
      prefix: n.prefix || 'INV', padding: n.padding || 3, next: n.next || 1,
    });
  }, [profile]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/portal/contractor-profile', {
        cis_status: form.cis_status,
        invoice_day_rate: form.invoice_day_rate === '' ? null : parseFloat(form.invoice_day_rate),
        payment_terms_days: parseInt(form.payment_terms_days, 10),
        invoice_numbering: { prefix: form.prefix, padding: parseInt(form.padding, 10), next: parseInt(form.next, 10) },
      });
      toast.success('Invoice settings saved');
      onSaved?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="bg-[#2A3240] border-white/10" data-testid="invoice-settings-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-white text-lg flex items-center gap-2"><Percent className="w-5 h-5 text-[#F5A623]" /> CIS & invoice settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-gray-300 text-sm">CIS deduction rate</Label>
          <div className="grid grid-cols-3 gap-2 mt-1" data-testid="cis-rate-options">
            {CIS_OPTIONS.map(o => (
              <button key={o.value} type="button" onClick={() => setForm(f => ({ ...f, cis_status: o.value }))}
                className={`rounded-xl border p-3 text-left min-h-[64px] active:scale-95 transition ${form.cis_status === o.value ? 'border-[#F5A623] bg-[#F5A623]/10 text-white' : 'border-white/15 bg-[#1E2530] text-gray-300'}`}
                data-testid={`cis-rate-${o.value}`}>
                <div className="font-bold text-base">{o.label}</div>
                <div className="text-[11px] text-gray-400 leading-tight">{o.hint}</div>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-gray-500 mt-1">Deducted from labour (and fuel/travel) on the CIS box of your invoice. Materials are never deducted. No VAT.</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-gray-300 text-sm flex items-center gap-1"><PoundSterling className="w-3.5 h-3.5" /> Default day rate</Label>
            <Input type="number" step="0.01" inputMode="decimal" value={form.invoice_day_rate} onChange={e => setForm(f => ({ ...f, invoice_day_rate: e.target.value }))} className={field} data-testid="invoice-day-rate" />
            {profile?.costing_day_rate != null && <p className="text-[11px] text-gray-500 mt-1">Pre-filled on each labour line; editable per invoice.</p>}
          </div>
          <div>
            <Label className="text-gray-300 text-sm">Payment terms (days)</Label>
            <Input type="number" min="0" max="120" value={form.payment_terms_days} onChange={e => setForm(f => ({ ...f, payment_terms_days: e.target.value }))} className={field} data-testid="invoice-terms-days" />
          </div>
        </div>
        <div>
          <Label className="text-gray-300 text-sm flex items-center gap-1"><Hash className="w-3.5 h-3.5" /> Invoice number format</Label>
          <div className="grid grid-cols-3 gap-2 mt-1">
            <div><span className="text-[11px] text-gray-500">Prefix</span><Input value={form.prefix} onChange={e => setForm(f => ({ ...f, prefix: e.target.value.toUpperCase() }))} className="h-12 bg-[#1E2530] border-white/20 text-white" data-testid="invoice-prefix" /></div>
            <div><span className="text-[11px] text-gray-500">Digits</span><Input type="number" min="1" max="6" value={form.padding} onChange={e => setForm(f => ({ ...f, padding: e.target.value }))} className="h-12 bg-[#1E2530] border-white/20 text-white" data-testid="invoice-padding" /></div>
            <div><span className="text-[11px] text-gray-500">Next number</span><Input type="number" min="1" value={form.next} onChange={e => setForm(f => ({ ...f, next: e.target.value }))} className="h-12 bg-[#1E2530] border-white/20 text-white" data-testid="invoice-next" /></div>
          </div>
          <div className="mt-2 rounded-lg bg-[#1E2530] border border-white/10 p-3 flex items-center justify-between">
            <span className="text-xs text-gray-400">Your next invoice will be</span>
            <span className="text-lg font-bold text-[#F5A623] tabular-nums" data-testid="invoice-number-preview">{fmtNumber(form.prefix, form.padding, form.next)}</span>
          </div>
          <p className="text-[11px] text-gray-500 mt-1">Set the next number to carry on from your paper or Excel sequence. Drafts don't use a number; issued numbers are never reused.</p>
        </div>
        <Button onClick={save} disabled={saving} className="w-full h-12 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold" data-testid="save-invoice-settings-btn">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-5 h-5 mr-2" /> Save invoice settings</>}
        </Button>
      </CardContent>
    </Card>
  );
}
